import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';

// Components
import Grid from '@material-ui/core/Grid';
import Typography from 'components/CncUI/Typography';
import DataTable from 'components/CncUI/DataTable';
import Accordion from 'components/CncUI/Accordion';
import Button from 'components/CncUI/Button';
import FileDownloader from 'components/FileDownloader';
import FileViewer from 'components/FileViewer';
import ImageViewer from 'components/ImageViewer';
import EmptyMyFiles from './EmptyMyFiles';
import FileUploader from './FileUploader';

// Icons
import FolderIcon from '@material-ui/icons/Folder';
import EyeIcon from '@material-ui/icons/RemoveRedEye';
import DownloadIcon from '@material-ui/icons/GetApp';
import UploadIcon from '@material-ui/icons/Publish';

// Hooks
import useBrowserMatchVersion from 'hooks/useBrowserMatchVersion';
import useGetMySupportingDocumentFile from './hooks/useGetMySupportingDocumentFile';

const CATEGORIES = [
  { displayName: 'Box Rental', key: 'box_rental' },
  { displayName: 'Computer Rental', key: 'computer_rental' },
  { displayName: 'Car Allowance', key: 'car_allowance' },
  { displayName: 'Mobile Phone', key: 'mobile_phone' },
  { displayName: 'Housing Allowance', key: 'housing_allowance' },
  { displayName: 'Other', key: 'other' },
];

const useStyles = makeStyles(({ spacing }) => ({
  title: {
    marginTop: 0,
  },
  accordions: {
    marginTop: spacing(3),
    marginBottom: spacing(5),
  },
  accordionTable: {
    width: '100%',
  },
  clickable: {
    cursor: 'pointer',
  },
  uploadTrigger: {
    maxWidth: 250,
  },
}));

const SupportingDocuments = ({
  myFiles,
  isAuthenticated,
  fetchDataHandler,
}) => {
  const classes = useStyles();
  const [browserMatch] = useBrowserMatchVersion(
    ['safari', 'mobile safari'],
    ['<13', '<13'],
  );
  const [expanded, setExpanded] = useState('');
  const expand = panel => (_, isExpanded) =>
    setExpanded(isExpanded ? panel : '');
  const [state, setState] = useState({
    viewerOpen: false,
    imageViewerOpen: false,
    downloaderOpen: false,
    uploaderOpen: false,
    fileId: null,
  });
  const modify = data => setState(old => ({ ...old, ...data }));

  const toggleFilePreview = ({ fileId, fileName }) => {
    fileName.includes('pdf')
      ? modify({
          viewerOpen: true,
          fileId,
        })
      : modify({
          imageViewerOpen: true,
          fileId,
        });
  };

  const toggleDownloader = ({ fileId }) =>
    modify({ downloaderOpen: true, fileId });

  const toggleUploader = () => modify({ uploaderOpen: true });

  const date = field => offer =>
    moment(offer[field], 'YYYY-MM-DD HH:mm:ss').format('MM-DD-YYYY');

  const columns = [
    { field: 'name', label: 'File Name' },
    {
      field: 'createdAt',
      label: 'Date Uploaded',
      value: date('createdAt'),
    },
    {
      field: 'actions',
      label: ' ',
      sortable: false,
      headerProps: { style: { minWidth: 100 } },
      value: ({ id: fileId, name: fileName }) => (
        <Grid
          container
          spacing={3}
          alignItems="center"
          justify="flex-end"
          id={`Actions-doc-${fileId}`}
        >
          {!browserMatch && (
            <Grid item>
              <EyeIcon
                id={`View-doc-${fileId}`}
                title="View doc"
                className={classes.clickable}
                onClick={() => toggleFilePreview({ fileId, fileName })}
              />
            </Grid>
          )}
          <Grid item>
            <DownloadIcon
              id={`Download-doc-${fileId}`}
              title="Download doc"
              className={classes.clickable}
              onClick={() => toggleDownloader({ fileId })}
            />
          </Grid>
        </Grid>
      ),
    },
  ];

  const {
    viewerOpen,
    imageViewerOpen,
    downloaderOpen,
    uploaderOpen,
    fileId,
  } = state;

  return (
    <>
      <Typography variant="h3" className={classes.title}>
        Supporting Documents
      </Typography>
      <Typography variant="body2">Manage your supporting documents.</Typography>
      {viewerOpen && (
        <FileViewer
          fileFetcher={useGetMySupportingDocumentFile}
          config={{ fileId, isPreview: true }}
          onClose={() =>
            modify({
              viewerOpen: false,
              fileId: null,
            })
          }
        />
      )}
      {imageViewerOpen && (
        <ImageViewer
          fileFetcher={useGetMySupportingDocumentFile}
          config={{ fileId }}
          onClose={() =>
            modify({
              imageViewerOpen: false,
              fileId: null,
            })
          }
        />
      )}
      {downloaderOpen && (
        <FileDownloader
          urlFetcher={useGetMySupportingDocumentFile}
          config={{ fileId }}
          onClose={() =>
            modify({
              downloaderOpen: false,
              fileId: null,
            })
          }
        />
      )}
      {isAuthenticated && uploaderOpen && (
        <FileUploader
          previewFileHandler={toggleFilePreview}
          filesAddedHandler={fetchDataHandler}
          onClose={() =>
            modify({
              uploaderOpen: false,
              fileId: null,
            })
          }
          category={expanded}
        />
      )}
      <Grid item xs={12} className={classes.accordions}>
        {CATEGORIES.map(({ key, displayName }) => {
          const fileList = myFiles.filter(file => file.category === key);
          return (
            <Accordion
              key={key}
              expanded={expanded === key}
              onChange={expand(key)}
            >
              <Accordion.Title Icon={<FolderIcon />} expandedAlign={'right'}>
                {displayName} ({fileList.length})
              </Accordion.Title>
              <Accordion.Content>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {fileList.length === 0 ? (
                      <EmptyMyFiles
                        type="supportingDocuments"
                        border={false}
                        blurb="You currently have no files."
                        blurbLine2={
                          <>
                            We support these formats:
                            <br />
                            JPG, PNG, PDF
                          </>
                        }
                      />
                    ) : (
                      <DataTable
                        id={`${key}-document-table`}
                        transparent
                        columns={columns}
                        data={fileList}
                        sort="createdAt"
                        order="desc"
                        emptyRows="There are no files to display."
                        className={classes.accordionTable}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      className={classes.uploadTrigger}
                      onClick={toggleUploader}
                      startIcon={<UploadIcon />}
                    >
                      Upload New File
                    </Button>
                  </Grid>
                </Grid>
              </Accordion.Content>
            </Accordion>
          );
        })}
      </Grid>
    </>
  );
};

export default SupportingDocuments;
